import { Element, useEditor, useNode } from "@craftjs/core";
import * as React from "react";
import { useSelector } from "react-redux";
import type { Speaker } from "src/types";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import Text from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
} from "@/components/editor/shared/block-wrapper";
import SyncedSpeakersSettings from "@/components/editor/shared/speakers/speakers-settings/synced-speakers-settings-component";
import { PaddingWrapper } from "@/components/editor/themes/crosby/constants";
import { getAllSpeakers } from "@/features/speakers/speakers-reducer";

import SyncedSpeakerCard from "./speaker-card/synced-speaker-card-component";
import { SpeakerList } from "./speakers-component-styles";

type SyncedSpeakersProps = {
  speakers?: Speaker[];
};

export default function SyncedSpeakers(props: SyncedSpeakersProps) {
  const {
    connectors: { connect },
    id: nodeId,
  } = useNode();
  const dashboardSpeakers = useSelector(getAllSpeakers);
  const speakers = props.speakers ?? dashboardSpeakers;

  const isEmpty = speakers.length === 0;
  const { enabled } = useEditor((state) => ({
    enabled: state.isEnabled(),
  }));

  if (!enabled && isEmpty) return null;

  return (
    <BlockBackgroundColorWrapper ref={connect} id={nodeId}>
      <PaddingWrapper>
        <BlockMaxWidthWrapper maxWidth="1248px">
          <div className="flex justify-center">
            <Element
              id="speakers-title"
              is={Text}
              element="h2"
              text="Speakers"
              pattern="headingTwo"
              data-testid="speakers-title"
            />
          </div>
          <SpeakerList>
            {enabled && isEmpty && <SyncedSpeakersSettings minimal />}
            {(speakers || []).map((speaker, idx) => (
              <SyncedSpeakerCard speaker={speaker} key={idx} />
            ))}
          </SpeakerList>
        </BlockMaxWidthWrapper>
      </PaddingWrapper>
    </BlockBackgroundColorWrapper>
  );
}

SyncedSpeakers.craft = {
  isCanvas: true,
  custom: {
    displayName: "Synced Speakers",
  },
  related: {
    leftOutlineOptions: TopLevelFloatingOptions,
    settings: () => SyncedSpeakersSettings({}),
  },
};
